import { useEffect, useRef } from "react";

/**
 * Hook to detect when the React client becomes inactive (e.g., no user interaction for a specified timeout).
 * @param onInactive
 * @param timeoutMs - The timeout duration in milliseconds after which the client is considered inactive.
 * @param hookDisabled
 * @param onInitAndActiveAgain - Optional callback invoked when the client becomes active again after being inactive.
 */
export const useOnReactClientInactive = ({
	onInactive,
	timeoutMs,
	hookDisabled,
	onInitAndActiveAgain,
}: {
	onInactive: () => void;
	timeoutMs: number;
	hookDisabled?: boolean;
	onInitAndActiveAgain?: () => void;
}) => {
	const isInitializedRef = useRef(false);
	const isCurrentlyInactiveRef = useRef(false);

	useEffect(() => {
		if (hookDisabled) {
			// Reset session state so a later re-enable — e.g. a different user logging in on the same tab
			// after logout stopped the previous recording — is treated as a fresh start and re-invokes
			// onInitAndActiveAgain (which restarts session recording). Without this the refs persist across
			// the disabled window and the re-enable is misread as "already active", so recording never
			// restarts for the new session.
			isInitializedRef.current = false;
			isCurrentlyInactiveRef.current = false;
			return () => {};
		}

		let loopTimeout: ReturnType<typeof setTimeout>;
		let lastInteraction = 0;
		const MIN_INTERVAL = 10 * 1000; // 10 seconds

		const handleSomeInteraction = () => {
			const now = Date.now();

			if (!isInitializedRef.current) {
				// first time ever
				isInitializedRef.current = true;
				isCurrentlyInactiveRef.current = false;
				onInitAndActiveAgain?.();
			} else if (isCurrentlyInactiveRef.current) {
				const hasInteractedAgain = now - lastInteraction >= MIN_INTERVAL;
				if (!hasInteractedAgain) {
					return; // ignore noisy events
				}
				onInitAndActiveAgain?.();
				isCurrentlyInactiveRef.current = false;
			}

			lastInteraction = now;
			clearTimeout(loopTimeout);
			loopTimeout = setTimeout(() => {
				isCurrentlyInactiveRef.current = true;
				onInactive();
			}, timeoutMs);
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				handleSomeInteraction();
			}
		};

		handleSomeInteraction();
		document.addEventListener("visibilitychange", handleVisibilityChange);
		window.addEventListener("mousemove", handleSomeInteraction);
		window.addEventListener("keydown", handleSomeInteraction);
		window.addEventListener("touchstart", handleSomeInteraction);

		return () => {
			clearTimeout(loopTimeout);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("mousemove", handleSomeInteraction);
			window.removeEventListener("keydown", handleSomeInteraction);
			window.removeEventListener("touchstart", handleSomeInteraction);
		};
	}, [onInactive, timeoutMs, hookDisabled, onInitAndActiveAgain]);
};
